import type { ExtractedFact } from '@karehero/models';

import {
  HELP_TIME_ID,
  NEEDS_NO_HELP,
  type BenefitsCalculatorAnswers,
} from 'config/benefitsCalculatorV2Steps';
import type { QuestionsMode } from 'config/onboardingV4/flows';
import type { QuestionCondition, TriageQuestion } from 'config/triageQuestions';
import { optionIDsOf } from 'utils/extractedFacts';

// What is known for the triage questions, from taps and from what they said,
// and which question that leaves to ask.

/** What is known for each question: tapped answers, and those read from what they said. */
export interface Knowledge {
  answers: BenefitsCalculatorAnswers;
  predicted: BenefitsCalculatorAnswers;
  saidInOwnWords: readonly string[];
}

/** The labels of a question's options, by option id. */
export const optionLabels = (
  question: TriageQuestion,
  optionIDs: readonly string[],
) =>
  question.options
    .filter((option) => optionIDs.includes(option.id))
    .map((option) => option.label)
    .join(', ');

/** The answer known for a question: tapped, or else read from what they said. */
export const knownAnswer = (knowledge: Knowledge, questionID: string) =>
  knowledge.answers[questionID] ?? knowledge.predicted[questionID];

const knownAnswers = (knowledge: Knowledge): BenefitsCalculatorAnswers => ({
  ...knowledge.predicted,
  ...knowledge.answers,
});

/** Whether a condition's answer is in; one not yet answered meets no condition. */
export const isConditionMet = (
  condition: QuestionCondition,
  answers: BenefitsCalculatorAnswers,
): boolean => {
  const answer = answers[condition.questionKey];
  if (!answer) return false;
  const wanted = Array.isArray(condition.value)
    ? condition.value
    : [condition.value];
  const isAny = wanted.some((id) => answer.includes(id));
  return condition.operator === 'not-equal' ? !isAny : isAny;
};

/** The first question unanswered, not already known in `gaps` mode, and called for. */
export const nextQuestion = (
  questions: readonly TriageQuestion[],
  mode: QuestionsMode,
  knowledge: Knowledge,
): TriageQuestion | null => {
  const known = knownAnswers(knowledge);
  return (
    questions.find((question) => {
      if (knowledge.answers[question.id]) return false;
      if (knowledge.saidInOwnWords.includes(question.id)) return false;
      if (mode === 'gaps' && knowledge.predicted[question.id]) return false;
      return !question.condition || isConditionMet(question.condition, known);
    }) ?? null
  );
};

/** How many questions are left to ask, for saying so before the first. */
export const questionsLeft = (
  questions: readonly TriageQuestion[],
  mode: QuestionsMode,
  knowledge: Knowledge,
): number => {
  let left = 0;
  let asked = knowledge;
  for (let question = nextQuestion(questions, mode, asked); question;) {
    left += 1;
    asked = {
      ...asked,
      saidInOwnWords: [...asked.saidInOwnWords, question.id],
    };
    question = nextQuestion(questions, mode, asked);
  }
  return left;
};

/** The answers the facts read from what they said predict, by question; listed oldest first, the latest fact wins. */
export const predictedAnswers = (
  facts: readonly ExtractedFact[],
  questionIDs: readonly string[],
): BenefitsCalculatorAnswers => {
  const predicted: BenefitsCalculatorAnswers = {};
  for (const fact of facts) {
    if (fact.dismissedAt) continue;
    const optionIDs = optionIDsOf(fact);
    if (!optionIDs.length) continue;
    for (const { questionId } of fact.mappings) {
      if (questionIDs.includes(questionId)) predicted[questionId] = optionIDs;
    }
  }
  return predicted;
};

/** Whether the option is among what is known for the question. */
export const isAnswer = (
  knowledge: Knowledge,
  questionID: string,
  optionID: string,
) => knownAnswer(knowledge, questionID)?.includes(optionID) ?? false;

export const needsNoHelp = (knowledge: Knowledge) =>
  isAnswer(knowledge, HELP_TIME_ID, NEEDS_NO_HELP);
